(function () {

  const CONFIG = {

    textBox: {
      fieldSelector: ".plan-text-box",
      errorClass: "plan-text-box--error",
      inputSelector: ".plan-text-box__input",
      validationSelector: ".plan__validation"
    },

    choice: {
      fieldSelector: ".bg-choice",
      errorClass: "bg-choice--error",
      inputSelector: "input[type='checkbox'], input[type='radio'], input",
      validationSelector: ".plan__validation"
    },

    oneQuestionRow: {
      fieldSelector: ".quote-redesign-one-question-row",
      errorClass: "required--error",
      inputSelector: "input, textarea, select",
      validationSelector: ".plan__validation"
    },

    questionDiv: {
      fieldSelector: ".question-div",
      errorClass: "required--error",
      inputSelector: "input:not([type='hidden']), textarea, select",
      validationSelector: ".plan__validation"
    },

    conditionSelectWrapper: {
      fieldSelector: ".condition-select-wrapper",
      errorClass: "required--error",
      inputSelector: "select",
      validationSelector: ".plan__validation"
    },

    yesNoGroup: {
      fieldSelector: ".radio-btns",
      errorClass: "bg-choice--error",
      errorDescendantSelector: ".bg-choice",
      inputSelector: "input[type='radio']",
      validationSelector: ".plan__validation",
      validationSiblingSelector: ".plan__validation",
      ariaTarget: "container",
      ensureRole: "radiogroup",
      errorDrivenByClassOnly: true
    },

    contactError: {
      fieldSelector: ".quote-error.error-contact-member.quote-redesign-error",
      allowMultipleValidations: true,
      validationSelector: "p[role='alert']",
      inputSelector: null
    },

    validationIdPrefixes: [
      "choicePlanId_",
      "question",
      "plan",
      "validation",
      "textbox-validation",
      "choice-validation",
      "onequestion-validation",
      "questiondiv-validation",
      "conditionselect-validation",
      "yesno-validation",
      "contacterror-validation"
    ],

    continueButtonSelector: ".quote-redesign-react-continue"
  };


  /* ---------------------------------------------------------------
     UTILITIES
  ----------------------------------------------------------------*/

  function isVisible(el) {
    if (!el) return false;
    const s = window.getComputedStyle(el);
    return !(s.display === "none" || s.visibility === "hidden" || s.opacity === "0");
  }

  function ensureValidId(el, prefix) {
    if (!el.id) {
      el.id = `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
    } else {
      el.id = el.id.replace(/\s+/g, "_");
    }
    return el.id;
  }

  function getTokens(el) {
    return (el.getAttribute("aria-describedby") || "")
      .split(" ")
      .map(x => x.trim())
      .filter(Boolean);
  }

  function setTokens(el, tokens) {
    const clean = [...new Set(tokens)];
    if (clean.length) el.setAttribute("aria-describedby", clean.join(" "));
    else el.removeAttribute("aria-describedby");
  }

  function addToken(el, id) {
    const tokens = getTokens(el);
    if (!tokens.includes(id)) tokens.push(id);
    setTokens(el, tokens);
  }

  function removeToken(el, id) {
    const tokens = getTokens(el).filter(x => x !== id);
    setTokens(el, tokens);
  }

  function cleanup(el) {
    const tokens = getTokens(el).filter(id => document.getElementById(id));
    setTokens(el, tokens);
    if (!tokens.length) el.removeAttribute("aria-invalid");
  }


  /* ---------------------------------------------------------------
     CONTACT ERROR – FIND INPUT ABOVE
  ----------------------------------------------------------------*/

  function findInputForContactError(fieldEl) {

    let prev = fieldEl.previousElementSibling;
    if (prev && prev.matches("input, input[type='email'], input[type='tel']")) return prev;

    if (prev) {
      const insidePrev = prev.querySelector("input[type='email'], input[type='tel'], input");
      if (insidePrev) return insidePrev;
    }

    let parent = fieldEl.parentElement;
    while (parent) {
      let ps = parent.previousElementSibling;
      while (ps) {
        const found = ps.querySelector("input[type='email'], input[type='tel'], input");
        if (found) return found;
        ps = ps.previousElementSibling;
      }

      const insideParent = parent.querySelector("input[type='email'], input[type='tel'], input");
      if (insideParent) return insideParent;

      parent = parent.parentElement;
    }

    return null;
  }


  /* ---------------------------------------------------------------
     FIND VALIDATION NODE FOR YES/NO
  ----------------------------------------------------------------*/

  function findValidationNode(fieldEl, cfg) {

    const inside = fieldEl.querySelector(cfg.validationSelector);
    if (inside) return inside;

    let sib = fieldEl.nextElementSibling;
    while (sib) {
      if (sib.matches?.(cfg.validationSelector)) return sib;
      sib = sib.nextElementSibling;
    }

    sib = fieldEl.previousElementSibling;
    while (sib) {
      if (sib.matches?.(cfg.validationSelector)) return sib;
      sib = sib.previousElementSibling;
    }

    const parent = fieldEl.parentElement;
    if (parent) {

      let ps = parent.nextElementSibling;
      while (ps) {
        if (ps.matches?.(cfg.validationSelector)) return ps;
        ps = ps.nextElementSibling;
      }

      ps = parent.previousElementSibling;
      while (ps) {
        if (ps.matches?.(cfg.validationSelector)) return ps;
        ps = ps.previousElementSibling;
      }
    }

    return null;
  }


  /* ---------------------------------------------------------------
     CORE SYNC (FINAL PATCHED NO-ARIA-ON-RADIO VERSION)
  ----------------------------------------------------------------*/

  function syncField(fieldEl, cfg, typeName) {

    const isYesNo = (typeName === "yesno");

    let inputs = [];

    if (typeName === "contacterror") {
      const inp = findInputForContactError(fieldEl);
      if (inp) inputs = [inp];
    }

    else if (isYesNo) {
      inputs = Array.from(fieldEl.querySelectorAll("input[type='radio']"));
    }

    else {
      const inp = fieldEl.querySelector(cfg.inputSelector);
      if (inp) inputs = [inp];
    }

    /* ✅ HARD BLOCK: ALWAYS REMOVE ARIA FROM RADIO INPUTS */
    if (isYesNo) {
      inputs.forEach(inp => {
        inp.removeAttribute("aria-describedby");
        inp.removeAttribute("aria-invalid");
      });
    }

    if (!inputs.length && !isYesNo) return;

    let validations = [];

    if (cfg.allowMultipleValidations) {
      validations = Array.from(fieldEl.querySelectorAll(cfg.validationSelector));
    } else {
      const node = findValidationNode(fieldEl, cfg);
      if (node) validations = [node];
    }

    if (!validations.length) {
      cleanupField(typeName, inputs, fieldEl);
      return;
    }

    let hasError = false;

    validations.forEach(val => {

      const vid = ensureValidId(val, `${typeName}-validation`);
      const visible = isVisible(val);

      if (visible) {
        hasError = true;

        if (isYesNo) {
          /* ✅ APPLY ARIA ONLY TO RADIO GROUP */
          fieldEl.setAttribute("role", "radiogroup");
          fieldEl.setAttribute("aria-describedby", vid);
          fieldEl.setAttribute("aria-invalid", "true");
        }

        /* =====================================================
           ✅ ADDITION START – aria-describedby on INPUTS
        ===================================================== */
        if (!isYesNo) {
          inputs.forEach(inp => {
            addToken(inp, vid);
            inp.setAttribute("aria-invalid", "true");
          });
        }
        /* =====================================================
           ✅ ADDITION END
        ===================================================== */

      } else {

        if (isYesNo) {
          fieldEl.removeAttribute("aria-describedby");
          fieldEl.removeAttribute("aria-invalid");
        }

        /* =====================================================
           ✅ ADDITION START – cleanup when error hidden
        ===================================================== */
        if (!isYesNo) {
          inputs.forEach(inp => {
            removeToken(inp, vid);
            inp.removeAttribute("aria-invalid");
          });
        }
        /* =====================================================
           ✅ ADDITION END
        ===================================================== */
      }
    });
    document.querySelectorAll('.bg-choice--error').forEach(choice => {
      const radio = choice.querySelector('input[type="radio"]');
      const visibleLabel = choice.querySelector('label').textContent;

      if (!radio) return;

      // ===== Focus: inject error as label =====
      choice.addEventListener('focusin', () => {
        const radioGroup = choice.closest('.radio-btns');
        if (!radioGroup) return;

        const error = radioGroup.parentElement?.querySelector('.plan__validation');
        if (!error) return;

        const errorText = error.textContent.trim() + " " + visibleLabel;
        if (!errorText || !error.id) return;
        const errorId = error.id; 

        const groupChoices = radioGroup.querySelectorAll('.bg-choice--error');

        groupChoices.forEach(groupChoice => {
          const groupRadio = groupChoice.querySelector('input[type="radio"]');
          const groupLabel = groupChoice.querySelector('label');

          if (groupLabel) {
            groupLabel.setAttribute('aria-hidden', 'true');
          }

          if (groupRadio) {
            groupRadio.setAttribute('aria-label', errorText);
            groupRadio.setAttribute('aria-invalid', 'true');
            groupRadio.removeAttribute('aria-describedby');
            setTimeout(() => {
              groupRadio.setAttribute('aria-describedby', errorId);
            }, 0);
          }
        });
      });

      // ✅ Remove ARIA from ALL options once ANY option is selected
      radio.addEventListener('change', () => {
        const radioGroup = choice.closest('.radio-btns');
        if (!radioGroup) return;

        const groupChoices = radioGroup.querySelectorAll('.bg-choice');

        groupChoices.forEach(groupChoice => {
          const groupRadio = groupChoice.querySelector('input[type="radio"]');
          const groupLabel = groupChoice.querySelector('label');

          if (groupRadio) {
            groupRadio.removeAttribute('aria-label');
            groupRadio.removeAttribute('aria-invalid');
            groupRadio.removeAttribute('aria-describedby');
          }

          if (groupLabel) {
            groupLabel.removeAttribute('aria-hidden');
          }
        });
      });
    });

    if (!hasError) cleanupField(typeName, inputs, fieldEl);
  }


  function cleanupField(typeName, inputs, fieldEl) {

    const isYesNo = (typeName === "yesno");

    if (isYesNo) {
      fieldEl.removeAttribute("aria-describedby");
      fieldEl.removeAttribute("aria-invalid");
    } else {
      inputs.forEach(inp => cleanup(inp));
    }
  }


  /* ---------------------------------------------------------------
     SYNC ALL
  ----------------------------------------------------------------*/

  function syncAll(root) {

    root.querySelectorAll(CONFIG.textBox.fieldSelector)
      .forEach(el => syncField(el, CONFIG.textBox, "textbox"));

    root.querySelectorAll(CONFIG.choice.fieldSelector)
      .forEach(el => syncField(el, CONFIG.choice, "choice"));

    root.querySelectorAll(CONFIG.oneQuestionRow.fieldSelector)
      .forEach(el => syncField(el, CONFIG.oneQuestionRow, "onequestion"));

    root.querySelectorAll(CONFIG.questionDiv.fieldSelector)
      .forEach(el => syncField(el, CONFIG.questionDiv, "questiondiv"));

    root.querySelectorAll(CONFIG.conditionSelectWrapper.fieldSelector)
      .forEach(el => syncField(el, CONFIG.conditionSelectWrapper, "conditionselect"));

    root.querySelectorAll(CONFIG.yesNoGroup.fieldSelector)
      .forEach(el => syncField(el, CONFIG.yesNoGroup, "yesno"));

    root.querySelectorAll(CONFIG.contactError.fieldSelector)
      .forEach(el => syncField(el, CONFIG.contactError, "contacterror"));
  }


  /* ---------------------------------------------------------------
     EVENT HANDLERS
  ----------------------------------------------------------------*/

  function closestField(el) {
    return (
      el.closest(CONFIG.textBox.fieldSelector) ||
      el.closest(CONFIG.choice.fieldSelector) ||
      el.closest(CONFIG.oneQuestionRow.fieldSelector) ||
      el.closest(CONFIG.questionDiv.fieldSelector) ||
      el.closest(CONFIG.conditionSelectWrapper.fieldSelector) ||
      el.closest(CONFIG.yesNoGroup.fieldSelector) ||
      el.closest(CONFIG.contactError.fieldSelector)
    );
  }

  function fieldTypeForEl(el) {
    if (el.matches(CONFIG.textBox.fieldSelector)) return ["textbox", CONFIG.textBox];
    if (el.matches(CONFIG.choice.fieldSelector)) return ["choice", CONFIG.choice];
    if (el.matches(CONFIG.oneQuestionRow.fieldSelector)) return ["onequestion", CONFIG.oneQuestionRow];
    if (el.matches(CONFIG.questionDiv.fieldSelector)) return ["questiondiv", CONFIG.questionDiv];
    if (el.matches(CONFIG.conditionSelectWrapper.fieldSelector)) return ["conditionselect", CONFIG.conditionSelectWrapper];
    if (el.matches(CONFIG.yesNoGroup.fieldSelector)) return ["yesno", CONFIG.yesNoGroup];
    if (el.matches(CONFIG.contactError.fieldSelector)) return ["contacterror", CONFIG.contactError];
    return null;
  }

  document.addEventListener("focusout", e => {
    const fld = closestField(e.target);
    if (!fld) return;
    const info = fieldTypeForEl(fld);
    if (info) syncField(fld, info[1], info[0]);
  }, true);

  document.addEventListener("change", e => {
    const fld = closestField(e.target);
    if (!fld) return;
    const info = fieldTypeForEl(fld);
    if (info) syncField(fld, info[1], info[0]);
  }, true);


  /* ---------------------------------------------------------------
     OBSERVER
  ----------------------------------------------------------------*/

  const observer = new MutationObserver(() => {
    syncAll(document);
  });

  observer.observe(document.body, {
    subtree: true,
    attributes: true,
    attributeFilter: ["class", "style", "hidden"],
    childList: true
  });


  /* ---------------------------------------------------------------
     INIT
  ----------------------------------------------------------------*/

  function init() {
    syncAll(document);
  }

  if (document.readyState === "loading")
    document.addEventListener("DOMContentLoaded", init);
  else
    init();

})();

